import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MyReactSelect } from "../../inputs/inputs";
import { useDebouncedCallback } from "use-debounce";
import AdminLayout from "../../layout/AdminLayout";
import { useAuth } from "../../../context/AuthContext";
import * as model from "../../../models/UserModel";
import { apiError } from "../../../helpers/ErrorHelper";
import UserSessionLogsTable from "./UserSessionLogsTable";
import { isSuperAdminUser } from "../../../helpers/GeneralHelper";
import { useLoginActivityAccess } from "../../../hooks/useLoginActivityAccess";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { utcToLocal } from "../../../helpers/DateHelper";
import PageHeader from "../../common/PageHeader";
import ListFilterActions from "../../common/ListFilterActions";

const module = {
  title: "Login activity",
  route: "users",
};

const formatUserTypeLabel = (user) => {
  if (user?.urole_title) {
    return user.urole_title;
  }
  const type = user?.urole_type;
  if (!type) {
    return "";
  }
  return String(type)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const buildMemberOptionLabel = (user) => {
  const name =
    user.user_name ||
    [user.user_first_name, user.user_last_name].filter(Boolean).join(" ");
  const typeLabel = formatUserTypeLabel(user);
  const emailPart = user.user_email ? ` (${user.user_email})` : "";
  if (!name) {
    return `${typeLabel || "User"}${emailPart}` || `User #${user._user_id}`;
  }
  return `${name}${typeLabel ? ` · ${typeLabel}` : ""}${emailPart}`;
};

export default function UserSessionLogs() {
  const { state } = useAuth();
  const user_data = state.user_data;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionUserId =
    searchParams.get("user_id") || searchParams.get("session_user_id") || "";

  const [memberOptions, setMemberOptions] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [memberFilter, setMemberFilter] = useState({ session_member_id: null });

  const { canView, loading: accessLoading, access } = useLoginActivityAccess();
  const isSuperAdmin = isSuperAdminUser(user_data);
  const isManager = access?.scope === "team";

  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const logoutLabel = (type) => {
    if (!type) return "";
    const map = { manual: "Logged out", replaced: "New login", system: "System" };
    return map[type] || type;
  };

  const fetchData = async (newLimit, newPage) => {
    if (!canView) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await model.getSessionLogs({
        user_token: user_data.user_token,
        page: newPage,
        limit: newLimit,
        ...(sessionUserId
          ? { session_user_id: sessionUserId }
          : searchQuery
            ? { search: searchQuery }
            : {}),
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        ...(activeOnly ? { active_only: 1 } : {}),
      });
      setListData(response.session_logs || []);
      setTotalRows(response.pagination?.total_rows || 0);
      setPage(newPage);
      setLimit(newLimit);
    } catch (error) {
      apiError(error);
      if (error.response?.data?.invalid_token == 1) {
        navigate("/re-login");
      }
    }
    setLoading(false);
  };

  const debouncedSearch = useDebouncedCallback(() => fetchData(limit, 1), 500);

  const applyMemberFilter = (selected) => {
    setMemberFilter({ session_member_id: selected || null });
    setPage(1);
    if (!selected?.value) {
      const next = new URLSearchParams(searchParams);
      next.delete("user_id");
      next.delete("session_user_id");
      setSearchParams(next);
      return;
    }
    setSearchParams({ user_id: String(selected.value) });
  };

  const clearFilters = async () => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setActiveOnly(false);
    setMemberFilter({ session_member_id: null });
    setPage(1);
    setSearchParams({});
    setLoading(true);
    try {
      const response = await model.getSessionLogs({
        user_token: user_data.user_token,
        page: 1,
        limit,
      });
      setListData(response.session_logs || []);
      setTotalRows(response.pagination?.total_rows || 0);
    } catch (error) {
      apiError(error);
    }
    setLoading(false);
  };

  const hasFilters = Boolean(
    searchQuery || dateFrom || dateTo || activeOnly || sessionUserId
  );

  useEffect(() => {
    if (!canView) {
      return;
    }
    fetchData(limit, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, activeOnly, sessionUserId, canView, page]);

  useEffect(() => {
    if (sessionUserId) {
      return;
    }
    if (searchQuery !== "") {
      debouncedSearch();
    }
  }, [searchQuery, debouncedSearch, sessionUserId]);

  useEffect(() => {
    if (!canView) {
      return;
    }
    const loadMembers = async () => {
      setMembersLoading(true);
      try {
        const result = await model.getData({
          user_token: user_data.user_token,
          page: 1,
          limit: -1,
          user_is_active: 1,
          ...(isManager && user_data._user_id
            ? { user_manager_id: user_data._user_id }
            : {}),
        });
        const users = result?.users || [];
        const options = users
          .map((u) => ({
            value: u._user_id,
            label: buildMemberOptionLabel(u),
          }))
          .sort((a, b) => String(a.label).localeCompare(String(b.label)));
        setMemberOptions(options);
      } catch (error) {
        apiError(error);
      }
      setMembersLoading(false);
    };
    loadMembers();
  }, [canView, isManager, user_data.user_token, user_data._user_id]);

  useEffect(() => {
    if (!sessionUserId) {
      setMemberFilter({ session_member_id: null });
      return;
    }
    const match = memberOptions.find(
      (o) => String(o.value) === String(sessionUserId)
    );
    if (match) {
      setMemberFilter({ session_member_id: match });
    } else if (memberOptions.length > 0) {
      setMemberFilter({
        session_member_id: {
          value: sessionUserId,
          label: `User #${sessionUserId}`,
        },
      });
    }
  }, [sessionUserId, memberOptions]);

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const response = await model.getSessionLogs({
        user_token: user_data.user_token,
        page: 1,
        limit: 10000,
        export: 1,
        ...(sessionUserId
          ? { session_user_id: sessionUserId }
          : searchQuery
            ? { search: searchQuery }
            : {}),
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        ...(activeOnly ? { active_only: 1 } : {}),
      });
      const rows = response.session_logs || [];
      if (!rows.length) {
        toast.warning("No data to export");
        return;
      }
      const timestamp = new Date().toISOString().replace(/[-:T]/g, "").split(".")[0];
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Login activity");
      worksheet.columns = [
        { header: "User", key: "user_name", width: 24 },
        { header: "User type", key: "user_type", width: 18 },
        { header: "Email", key: "user_email", width: 28 },
        { header: "Logged in", key: "session_login_at", width: 22 },
        { header: "Logged out", key: "session_logout_at", width: 22 },
        { header: "End reason", key: "session_logout_type", width: 14 },
        { header: "IP", key: "session_ip_address", width: 16 },
        { header: "Browser", key: "session_browser_name", width: 14 },
        { header: "OS", key: "session_os", width: 14 },
      ];
      rows.forEach((row) => {
        worksheet.addRow({
          user_name: row.user_name,
          user_type: formatUserTypeLabel(row) || "—",
          user_email: row.user_email,
          session_login_at: utcToLocal(row.session_login_at),
          session_logout_at: row.session_logout_at
            ? utcToLocal(row.session_logout_at)
            : "Active",
          session_logout_type: logoutLabel(row.session_logout_type),
          session_ip_address: row.session_ip_address || "",
          session_browser_name: row.session_browser_name || "",
          session_os: row.session_os || "",
        });
      });
      worksheet.getRow(1).font = { bold: true };
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Login_Activity_${timestamp}.xlsx`
      );
    } catch (error) {
      apiError(error);
    }
    setExporting(false);
  };

  const runBackfill = async () => {
    if (
      !window.confirm(
        "Import past sessions from existing login tokens? Safe to run more than once; already imported tokens are skipped."
      )
    ) {
      return;
    }
    setBackfilling(true);
    try {
      const response = await model.backfillSessionLogs(user_data.user_token);
      if (response?.status) {
        const info = response.data?.backfill;
        toast.success(
          `Backfill done: ${info?.inserted ?? 0} added, ${info?.skipped ?? 0} skipped`
        );
        fetchData(limit, 1);
      } else {
        toast.error(response?.message || "Backfill failed");
      }
    } catch (error) {
      apiError(error);
    }
    setBackfilling(false);
  };

  if (accessLoading) {
    return (
      <AdminLayout>
        <div className="main-content app-content">
          <div className="container-fluid py-5 text-center text-muted">Loading…</div>
        </div>
      </AdminLayout>
    );
  }

  if (!canView) {
    return (
      <AdminLayout>
        <div className="main-content app-content">
          <div className="container-fluid py-5 text-center text-muted">
            {access?.reason === "no_direct_reports"
              ? "Login activity is not available because you have no team members assigned to you."
              : "You do not have access to login activity."}
            <div className="mt-3">
              <Link to="/users" className="btn btn-primary-light btn-sm">
                Back to team members
              </Link>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>{module.title} - Digital CMS</title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid">
          <PageHeader module={module} />

          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <p className="text-muted mb-0 fs-13">
              {isManager
                ? "Sign-in history for people who report to you."
                : "Track when team members sign in and when sessions end (logout or new login)."}
            </p>
            <Link to="/users" className="btn btn-outline-primary btn-sm">
              <i className="ri-arrow-left-line me-1 align-middle"></i>
              Team members
            </Link>
          </div>

          <div className="row">
            <div className="col-xl-12">
              <div className="card custom-card">
                <div className="card-header d-block">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <div>
                      <div className="card-title mb-1">Sessions</div>
                      <span className="text-muted fs-12">
                        {loading ? "Loading…" : `${totalRows.toLocaleString()} total`}
                      </span>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <ListFilterActions
                        showExport
                        onExport={exportToExcel}
                        showReset={hasFilters}
                        onReset={clearFilters}
                        exportLabel={exporting ? "Exporting…" : "Download Excel"}
                      />
                      <button
                        type="button"
                        className="btn btn-success-light btn-sm"
                        onClick={() => fetchData(limit, page)}
                        title="Refresh"
                      >
                        <i className="ri-refresh-line me-1 align-middle"></i>
                        Refresh
                      </button>
                      {isSuperAdmin && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={backfilling}
                          onClick={runBackfill}
                        >
                          <i className="ri-database-2-line me-1 align-middle"></i>
                          {backfilling ? "Importing…" : "Import history"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="row g-2 align-items-end">
                    <div className="col-lg-4 col-md-6">
                      <MyReactSelect
                        column="session_member_id"
                        label="Team member"
                        firstOption="All team members"
                        options={memberOptions}
                        fields={memberFilter}
                        setFields={setMemberFilter}
                        disabled={membersLoading}
                        onChange={applyMemberFilter}
                      />
                    </div>
                    <div className="col-lg-2 col-md-4 col-sm-6">
                      <label className="form-label fs-12 text-muted mb-1">From</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </div>
                    <div className="col-lg-2 col-md-4 col-sm-6">
                      <label className="form-label fs-12 text-muted mb-1">To</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                      />
                    </div>
                    <div className="col-lg-2 col-md-4 col-sm-6">
                      <label className="form-label fs-12 text-muted mb-1 d-block">Status</label>
                      <div className="form-check form-switch mb-0 ps-0">
                        <input
                          className="form-check-input ms-0 me-2"
                          type="checkbox"
                          id="session-active-only"
                          checked={activeOnly}
                          onChange={(e) => setActiveOnly(e.target.checked)}
                        />
                        <label className="form-check-label fs-13" htmlFor="session-active-only">
                          Active sessions only
                        </label>
                      </div>
                    </div>
                    {!sessionUserId && (
                      <div className="col-lg-4 col-md-12">
                        <label className="form-label fs-12 text-muted mb-1">Search</label>
                        <input
                          type="search"
                          className="form-control form-control-sm"
                          placeholder="Name, email, or mobile"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            if (e.target.value === "") {
                              fetchData(limit, 1);
                            }
                          }}
                          autoComplete="off"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-body pt-0">
                  <div className="table-responsive">
                    <UserSessionLogsTable
                      data={listData}
                      loading={loading}
                      totalRows={totalRows}
                      page={page}
                      limit={limit}
                      fetchData={fetchData}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
